import React from "react";
import {
    Dialog,
    IconButton,
    useTheme,
    Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import type { Settings } from "react-slick";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

interface ImageCarouselDialogProps {
    open: boolean;
    onClose: () => void;
    images?: string[];
}

export default function ImageCarouselDialog({
                                                open,
                                                onClose,
                                                images,
                                            }: ImageCarouselDialogProps) {
    const theme = useTheme();

    const displayImages = images?.length ? images : [];

    const sliderSettings: Settings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: true,
        adaptiveHeight: false,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            PaperProps={{
                sx: {
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    overflow: "hidden",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                },
            }}
            slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } } }}
        >
            <IconButton
                aria-label="Close image carousel"
                onClick={onClose}
                sx={{
                    position: "absolute",
                    top: theme.spacing(2),
                    right: theme.spacing(2),
                    zIndex: 2,
                    color: theme.palette.common.white,
                }}
            >
                <CloseIcon />
            </IconButton>

            {/* Backdrop click detection */}
            <Box
                onClick={onClose}
                sx={{
                    width: "100vw",
                    height: "100vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    px: 2,
                }}
            >
                <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        width: "100%",
                        maxWidth: 900,
                        height: {
                            xs: "80vh",
                            sm: "80vh",
                        },

                        ".slick-slider, .slick-list, .slick-track": {
                            height: "100%",
                        },
                        ".slick-slide": {
                            display: "flex !important",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                        },
                        ".slick-slide > div": {
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                        },

                        ".slick-dots li button:before": {
                            color: "#fff",
                            fontSize: "10px",
                        },
                    }}
                >
                    <Slider {...sliderSettings}>
                        {displayImages.map((src, idx) => (
                            <Box
                                key={idx}
                                component="img"
                                src={src}
                                alt={`Bild ${idx + 1} von ${displayImages.length}`}
                                sx={{
                                    maxWidth: "100%",
                                    maxHeight: {
                                        xs: "80vh",
                                        sm: "80vh",
                                    },
                                    height: "100%",
                                    objectFit: "contain",
                                    display: "block",
                                    margin: "0 auto",
                                }}
                            />
                        ))}
                    </Slider>
                </Box>
            </Box>
        </Dialog>
    );
};

const arrowButtonStyle = {
    width: 40,
    height: 40,
    borderRadius: "50%",
    position: "fixed",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    color: "white",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.6)",
    },
};

const NextArrow = (props: any) => {
    const { onClick } = props;
    return (
        <IconButton
            onClick={onClick}
            sx={{
                ...arrowButtonStyle,
                right: 16,
            }}
        >
            <ArrowForwardIosIcon />
        </IconButton>
    );
};

const PrevArrow = (props: any) => {
    const { onClick } = props;
    return (
        <IconButton
            onClick={onClick}
            sx={{
                ...arrowButtonStyle,
                left: 16,
            }}
        >
            <ArrowBackIosNewIcon />
        </IconButton>
    );
};
